import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getPrograms, getProgram, getProgramDay, enrollProgram, getUserEnrollments, getPremiumStatus, updatePreferences, getEntitlements } from '../lib/api';
import { SKETCH_PATTERNS, programImage } from '../lib/images';
import ProgramDayView from '../components/programs/ProgramDayView';
import MealPreferencesForm from '../components/onboarding/MealPreferencesForm';
import useRazorpay from '../hooks/useRazorpay';
import useCurrency from '../hooks/useCurrency';
import usePromoPreview from '../hooks/usePromoPreview';
import PriceWithPromo from '../components/PriceWithPromo';
import { trackEvent } from '../lib/firebase';
import { fbTrack } from '../lib/fbpixel';
import { Loader2, ChevronLeft, Clock, Star, Check, ChevronRight, Calendar, Lock, Crown, X, Sparkles } from 'lucide-react';

export default function ProgramsPage() {
  const { deviceId, user, preferences, refreshPreferences } = useAuth();
  const { pay, loading: payLoading, error: payError, authDialog } = useRazorpay();
  const currency = useCurrency();
  const promo = usePromoPreview();
  const [programs, setPrograms] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [entitlements, setEntitlements] = useState({ premium: false, programs: [], iq_report: false, personality_report: false });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [currentDay, setCurrentDay] = useState(1);
  const [loading, setLoading] = useState(true);
  const [dayLoading, setDayLoading] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [view, setView] = useState('list'); // list | detail | day
  const [prefGate, setPrefGate] = useState(null); // { action: fn } — pending action to run after prefs saved
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [progRes, enrollRes, premRes, entRes] = await Promise.allSettled([
          getPrograms(),
          getUserEnrollments(deviceId, user?.id || user?._id || user?.user_id),
          getPremiumStatus(deviceId),
          getEntitlements(deviceId, user?.email),
        ]);
        if (progRes.status === 'fulfilled') setPrograms(progRes.value.data || []);
        if (enrollRes.status === 'fulfilled') {
          const enrollData = enrollRes.value.data;
          setEnrollments(Array.isArray(enrollData) ? enrollData : (enrollData?.enrollments || []));
        }
        if (premRes.status === 'fulfilled') setPremiumStatus(premRes.value.data);
        if (entRes.status === 'fulfilled') setEntitlements(entRes.value.data || { premium: false, programs: [], iq_report: false, personality_report: false });
      } catch {}
      setLoading(false);
    }
    load();
  }, [deviceId, user]);

  const refreshEntitlements = async () => {
    try {
      const res = await getEntitlements(deviceId, user?.email);
      setEntitlements(res.data || { premium: false, programs: [], iq_report: false, personality_report: false });
    } catch {}
  };

  const isPremium = entitlements.premium || !!premiumStatus?.is_premium;
  const isProgramUnlocked = (prog) => !prog.price || prog.price === 0 || isPremium || (entitlements.programs || []).includes(prog.id);
  const isProgramLocked = (prog) => !isProgramUnlocked(prog);

  // Meal-preferences gate — run `action` only after the user explicitly saves prefs.
  // We can't rely on `preferences.cuisines.length` since the backend populates
  // sensible defaults for every user. Instead we use a per-device flag set
  // the moment the user saves via the form.
  const PREFS_FLAG = `today_meal_prefs_set_${deviceId}`;
  const hasMealPrefs = () => localStorage.getItem(PREFS_FLAG) === '1';

  const requirePrefs = useCallback((action) => {
    if (hasMealPrefs()) {
      action();
    } else {
      setPrefGate({ action });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const handleSavePrefs = async ({ dietary, cuisines, allergens }) => {
    // Capture pending action + mark prefs as set BEFORE any async work so that
    // a failing network call (e.g. updatePreferences race before guest-login
    // has fully registered on the remote) never strands the user with a closed
    // spinner and no Razorpay modal.
    const pending = prefGate?.action;
    localStorage.setItem(PREFS_FLAG, '1');
    setPrefGate(null);
    setSavingPrefs(false);
    // Start the purchase immediately — meal prefs are only used for later
    // meal personalization, not required for the payment flow.
    if (pending) pending();

    // Save prefs in the background (non-blocking). Errors are logged but don't
    // surface to the user because the purchase is already in flight.
    try {
      await updatePreferences({
        device_id: deviceId,
        meal_preferences: { dietary, cuisines, allergens },
      });
      trackEvent('meal_preferences_saved', {
        dietary,
        cuisines_count: (cuisines || []).length,
        allergens_count: (allergens || []).length,
        source: 'pre_program_gate',
      });
      if (refreshPreferences) refreshPreferences().catch(() => {});
    } catch (e) {
      console.warn('Meal preferences save failed (non-blocking)', e);
    }
  };

  const handleViewProgram = async (programId) => {
    setSelected(programId);
    setView('detail');
    setDetail(null);
    trackEvent('program_view', { program_id: programId });
    const prog = programs.find(p => p.id === programId);
    fbTrack('ViewContent', {
      content_ids: [programId],
      content_name: prog?.name || programId,
      content_category: 'program',
      content_type: 'product',
      value: Number(prog?.price || 0),
      currency: 'INR',
    });
    try {
      const res = await getProgram(programId, deviceId);
      setDetail(res.data);
    } catch {}
  };

  const handleEnroll = async (programId) => {
    requirePrefs(async () => {
      setEnrolling(true);
      try {
        await enrollProgram(programId, { device_id: deviceId });
        const prog = programs.find(p => p.id === programId);
        trackEvent('program_enroll', {
          program_id: programId,
          program_name: prog?.name || programId,
          duration_days: prog?.duration || null,
          // GA4 conversion shape — value defaults to 0 for free programs
          value: Number(prog?.price || 0),
          currency: 'INR',
          items: [{
            item_id: programId,
            item_name: prog?.name || programId,
            item_category: 'program',
            price: Number(prog?.price || 0),
            quantity: 1,
          }],
        });
        const res = await getUserEnrollments(deviceId, user?.id || user?._id || user?.user_id);
        const enrollData = res.data;
        setEnrollments(Array.isArray(enrollData) ? enrollData : (enrollData?.enrollments || []));
      } catch {}
      setEnrolling(false);
    });
  };

  const handleViewDay = async (programId, day) => {
    requirePrefs(async () => {
      setCurrentDay(day);
      setDayLoading(true);
      setView('day');
      try {
        const res = await getProgramDay(programId, day, deviceId);
        setDayData(res.data);
      } catch {}
      setDayLoading(false);
    });
  };

  const handleDayChange = async (newDay) => {
    if (newDay < 1 || newDay > (detail?.duration || 7)) return;
    setCurrentDay(newDay);
    setDayLoading(true);
    try {
      const res = await getProgramDay(selected, newDay, deviceId);
      setDayData(res.data);
    } catch {}
    setDayLoading(false);
  };

  const isEnrolled = (progId) => enrollments.some(e => e.program_id === progId && e.status === 'active');

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 max-w-6xl mx-auto" data-testid="programs-page">
      {authDialog}
      {/* LIST VIEW */}
      {view === 'list' && (
        <div className="animate-fade-in">
          <div className="rounded-lg overflow-hidden mb-8 relative h-40 keep-light-theme"
            style={{ background: 'linear-gradient(135deg, #F2EFE9 0%, #E5E1D8 50%, #D4CBB3 100%)' }}>
            <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url("${SKETCH_PATTERNS.leafBranch}")`, backgroundSize: '500px', backgroundPosition: 'right center', backgroundRepeat: 'no-repeat' }} />
            <div className="absolute inset-0 flex items-center px-8">
              <div>
                <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Wellness Programs</h1>
                <p className="text-sm" style={{ color: 'var(--muted-fg)' }}>Transform your life with guided wellness programs</p>
              </div>
            </div>
          </div>

          {/* Active Enrollments */}
          {enrollments.filter(e => e.status === 'active').length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Your Active Programs</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {enrollments.filter(e => e.status === 'active').map((e, i) => (
                  <button key={i} onClick={() => { handleViewProgram(e.program_id); }}
                    className="rounded-lg p-5 border flex items-center gap-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
                    style={{ background: 'var(--primary)' + '08', borderColor: 'var(--primary)' + '40' }} data-testid={`active-enrollment-${i}`}>
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
                      <Star className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-sm" style={{ color: 'var(--text)' }}>{e.program_name || e.program_id}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs" style={{ color: 'var(--muted-fg)' }}>Day {e.current_day || 1}</span>
                        <div className="flex-1 h-1.5 rounded-full" style={{ background: 'var(--muted)' }}>
                          <div className="h-full rounded-full" style={{ width: `${((e.current_day || 1) / (e.total_days || 7)) * 100}%`, background: 'var(--primary)' }} />
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 shrink-0" style={{ color: 'var(--muted-fg)' }} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* All Programs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.map((p, i) => {
              const locked = isProgramLocked(p);
              return (
                <button key={p.id} onClick={() => handleViewProgram(p.id)}
                  className={`rounded-lg border overflow-hidden text-left transition-all hover:-translate-y-1 hover:shadow-lg animate-fade-in stagger-${(i % 6) + 1}`}
                  style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid={`program-card-${p.id}`}>
                  <div className="h-40 bg-cover bg-center relative" style={{ backgroundImage: `url(${programImage(p.id)})` }}>
                    {locked && (
                      <div className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
                        <Lock className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: 'var(--secondary)', color: 'var(--secondary-fg)' }}>{p.category}</span>
                      {isEnrolled(p.id) && (
                        <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>Active</span>
                      )}
                      {locked && (
                        <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: 'var(--accent)', color: 'white' }} data-testid={`program-card-price-${p.id}`}>
                          {currency.format(promo.valid ? promo.apply(p.price) : p.price)}
                        </span>
                      )}
                    </div>
                    <h3 className="font-bold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{p.name}</h3>
                    <p className="text-sm mb-3 line-clamp-2" style={{ color: 'var(--muted-fg)' }}>{p.description}</p>
                    <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--muted-fg)' }}>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {p.duration} days</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* DETAIL VIEW */}
      {view === 'detail' && (
        <div className="animate-fade-in">
          <button onClick={() => { setView('list'); setDetail(null); }} className="flex items-center gap-1 text-sm mb-6" style={{ color: 'var(--primary)' }} data-testid="back-to-programs">
            <ChevronLeft className="w-4 h-4" /> Back to Programs
          </button>

          {!detail ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--primary)' }} />
            </div>
          ) : (
            <div className="max-w-3xl">
              <div className="h-56 rounded-lg bg-cover bg-center mb-6" style={{ backgroundImage: `url(${programImage(selected)})` }} />

              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: 'var(--secondary)', color: 'var(--secondary-fg)' }}>{detail.category}</span>
                <span className="text-xs flex items-center gap-1" style={{ color: 'var(--muted-fg)' }}><Clock className="w-3.5 h-3.5" /> {detail.duration} days</span>
              </div>

              <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }} data-testid="program-detail-title">{detail.name}</h1>
              <p className="mb-6 text-sm leading-relaxed" style={{ color: 'var(--muted-fg)' }}>{detail.description}</p>

              {/* Why This Program */}
              {detail.why_this_program && (
                <div className="rounded-lg p-5 border mb-6" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
                  <h3 className="font-bold text-sm mb-2" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Why This Program?</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-fg)' }}>{detail.why_this_program}</p>
                </div>
              )}

              {/* Focus Areas */}
              {detail.focus_areas && detail.focus_areas.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-bold text-sm mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Focus Areas</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {detail.focus_areas.map((f, i) => (
                      <div key={i} className="rounded-lg p-4 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                        <p className="font-bold text-sm mb-0.5" style={{ color: 'var(--text)' }}>{f.name}</p>
                        <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>{f.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detail.benefits && (
                <div className="mb-6">
                  <h3 className="font-bold text-sm mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Benefits</h3>
                  <div className="space-y-2">
                    {detail.benefits.map((b, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Check className="w-4 h-4" style={{ color: 'var(--primary)' }} />
                        <span className="text-sm" style={{ color: 'var(--text)' }}>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Day-by-Day Schedule */}
              {(!detail.price || detail.price === 0 || isPremium || (entitlements.programs || []).includes(selected)) && (
              <div className="mb-6" data-testid="day-schedule">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                  <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Daily Schedule</h3>
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {Array.from({ length: detail.duration }, (_, i) => i + 1).map(day => {
                    const enrollment = enrollments.find(e => e.program_id === selected && e.status === 'active');
                    const isCurrentDay = enrollment && enrollment.current_day === day;
                    const isPast = enrollment && enrollment.current_day > day;
                    return (
                      <button key={day} onClick={() => handleViewDay(selected, day)}
                        className="aspect-square rounded-lg border flex flex-col items-center justify-center text-xs font-medium transition-all hover:-translate-y-0.5 hover:shadow-sm"
                        style={{
                          background: isCurrentDay ? 'var(--primary)' : isPast ? 'var(--primary)' + '15' : 'var(--surface)',
                          color: isCurrentDay ? 'var(--primary-fg)' : isPast ? 'var(--primary)' : 'var(--text)',
                          borderColor: isCurrentDay ? 'var(--primary)' : 'var(--border)',
                        }}
                        data-testid={`day-btn-${day}`}>
                        {isPast && <Check className="w-3 h-3 mb-0.5" />}
                        <span className="font-bold">{day}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Enroll / View Today - locked behind payment if priced and not entitled */}
              {detail.price && detail.price > 0 && !isPremium && !(entitlements.programs || []).includes(selected) ? (
                <div className="rounded-lg p-6 border text-center"
                  style={{ background: 'linear-gradient(135deg, #879B7D10 0%, #E2725B10 100%)', borderColor: 'var(--accent)' + '40' }}
                  data-testid="program-paywall">
                  <Lock className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--accent)' }} />
                  <h3 className="font-bold text-lg mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
                    Unlock This Program
                  </h3>
                  <p className="text-sm mb-4" style={{ color: 'var(--muted-fg)' }}>
                    Get full access to all {detail.duration} days of guided activities, meals, workouts, and meditation.
                  </p>
                  <div className="flex items-center justify-center gap-4 mb-4 flex-wrap">
                    <PriceWithPromo inrAmount={detail.price} currency={currency} promo={promo} size="xl" testid="program-detail-price" />
                    <span className="text-xs px-2 py-1 rounded" style={{ background: 'var(--primary)' + '15', color: 'var(--primary)' }}>one-time</span>
                  </div>
                  {!currency.isINR && !currency.loading && (
                    <p className="text-xs mb-3" style={{ color: 'var(--muted-fg)' }}>
                      Billed in {currency.currency} · INR ₹{promo.valid ? Math.round(promo.apply(detail.price)) : detail.price}
                    </p>
                  )}
                  {payError && <p className="text-xs mb-3" style={{ color: '#DC2626' }}>{payError}</p>}
                  <button onClick={() => requirePrefs(() => pay({
                    deviceId, itemType: 'program', itemId: selected, amount: detail.price,
                    currency: currency.currency,
                    userName: user?.name, userEmail: user?.email,
                    allowGuest: true,
                    onSuccess: async () => {
                      // Also enroll so the program shows on Home → Active Programs
                      try { await enrollProgram(selected, { device_id: deviceId }); } catch {}
                      await refreshEntitlements();
                      try {
                        const res = await getUserEnrollments(deviceId, user?.id || user?._id || user?.user_id);
                        const enrollData = res.data;
                        setEnrollments(Array.isArray(enrollData) ? enrollData : (enrollData?.enrollments || []));
                      } catch {}
                    },
                  }))} disabled={payLoading} data-testid="buy-program-btn"
                    className="px-8 py-3 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 inline-flex items-center gap-2"
                    style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
                    {payLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                    {payLoading ? 'Processing...' : `Buy for ${currency.format(promo.valid ? promo.apply(detail.price) : detail.price)}`}
                  </button>
                  <p className="text-xs mt-3" style={{ color: 'var(--muted-fg)' }}>Or get all programs with Premium</p>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3">
                  {!isEnrolled(selected) ? (
                    <button onClick={() => handleEnroll(selected)} disabled={enrolling} data-testid="enroll-btn"
                      className="flex-1 py-3 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
                      style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
                      {enrolling && <Loader2 className="w-4 h-4 animate-spin" />}
                      Enroll & Start
                    </button>
                  ) : (
                    <div className="flex-1 flex items-center gap-2 px-4 py-3 rounded-lg" style={{ background: 'var(--primary)' + '15', color: 'var(--primary)' }}>
                      <Check className="w-5 h-5" /> Enrolled
                    </div>
                  )}
                  <button onClick={() => handleViewDay(selected, 1)} data-testid="view-day-1-btn"
                    className="px-6 py-3 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] flex items-center gap-2"
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                    <Calendar className="w-4 h-4" /> View Day 1
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* DAY VIEW */}
      {view === 'day' && (
        <div className="animate-fade-in">
          {dayLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--primary)' }} />
            </div>
          ) : (
            <ProgramDayView
              dayData={dayData}
              programId={selected}
              programName={detail?.name || ''}
              totalDays={detail?.duration || 7}
              currentDayNum={currentDay}
              onBack={() => setView('detail')}
              onDayChange={handleDayChange}
            />
          )}
        </div>
      )}

      {/* Pre-program Meal Preferences Gate */}
      {prefGate && (
        <PreProgramMealGate
          programName={detail?.name}
          saving={savingPrefs}
          onSubmit={handleSavePrefs}
          onClose={() => setPrefGate(null)}
        />
      )}
    </div>
  );
}

function PreProgramMealGate({ programName, saving, onSubmit, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      style={{ background: 'rgba(24, 29, 25, 0.55)', backdropFilter: 'blur(6px)' }}
      data-testid="pre-program-prefs-modal"
      onClick={onClose}>
      <div
        className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl border"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
        onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="close-prefs-gate"
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-black/10"
          style={{ color: 'var(--muted-fg)' }}>
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4" style={{ color: 'var(--primary)' }} />
            <p className="text-xs font-semibold uppercase tracking-widest"
              style={{ color: 'var(--primary)', fontFamily: 'Manrope' }}>
              Personalize your plan
            </p>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold mb-1"
            style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            Quick meal check before {programName || 'your program'}
          </h2>
          <p className="text-sm mb-5" style={{ color: 'var(--muted-fg)' }}>
            Takes under a minute. We'll tune each day's meals to your diet, cuisines, and allergens.
          </p>

          <MealPreferencesForm
            onSubmit={onSubmit}
            submitting={saving}
            submitLabel="Save & continue"
          />
        </div>
      </div>
    </div>
  );
}
